// packages/web/src/components/AdminProducts.tsx
import React, { useState, useEffect, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Image as ImageIcon,
  Layers,
  Sparkles,
  Check,
  X,
  AlertTriangle,
  Upload,
  ArrowLeft,
  Save,
  RefreshCw,
  Tag,
  DollarSign,
  Package,
  Power,
} from 'lucide-react'
import { apiGet, apiGetWithMeta, apiPost, apiPut, apiDelete, apiUpload } from '../lib/api'
import { CreateProductInput } from '@buddys-choice/shared'
import type { Product, ProductVariant, CreateProductInput as CreateProductInputType } from '@buddys-choice/shared'
import { useStoreCategories, getCategoryLabel } from '../lib/storeCategories'
import { ProductImage } from './ProductImage'
import { AppDataTable } from './AppDataTable'
import { assetSrc } from '../lib/assets'
import { DataTablePagination } from './DataTablePagination'
import { SearchableSelect } from './SearchableSelect'

const VARIANT_ATTR_OPTIONS = [
  { value: 'at-4', label: 'Chain Length' },
  { value: 'at-3', label: 'Color Finish' },
  { value: 'at-2', label: 'Material' },
  { value: 'at-1', label: 'Ring Size' },
]

// Mini single-attribute variant creation form — the UI only captures one
// attribute pair per variant, so this mirrors CreateVariantInput's field-level
// rules without its array-of-attributes shape.
const VariantFormSchema = z.object({
  sku: z.string().max(50).trim().toUpperCase().optional(),
  priceOverride: z.string().trim(),
  compareAtPrice: z.string().trim().optional(),
  stockQty: z.coerce.number().int().min(0, 'Stock must be 0 or more'),
  attrTypeId: z.string().min(1),
  attrValue: z.string().min(1, 'Attribute value is required'),
})
type VariantFormValues = z.infer<typeof VariantFormSchema>

type ProductGalleryImage = NonNullable<Product['images']>[number]

export const AdminProducts: React.FC = () => {
  const { categories } = useStoreCategories()
  // Page View Modes: 'list' (Table) | 'editor' (Full Page Form) | 'variants' (Full Page Variant Manager)
  const [viewMode, setViewMode] = useState<'list' | 'editor' | 'variants'>('list')
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string>('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)

  // Editor Form State
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)

  const {
    register: registerProduct,
    handleSubmit: handleProductFormSubmit,
    control: productControl,
    reset: resetProductForm,
    setValue: setProductValue,
    watch: watchProduct,
    formState: { errors: productErrors, isSubmitting: savingProduct },
  } = useForm<CreateProductInputType>({
    resolver: zodResolver(CreateProductInput) as import('react-hook-form').Resolver<CreateProductInputType>,
    defaultValues: {
      name: '',
      description: '',
      category: 'RINGS',
      basePrice: 0,
      compareAtPrice: undefined,
      sku: '',
      stockQty: 0,
      isActive: true,
      isFeatured: false,
      heroImageUrl: '',
    },
  })
  // File Upload State — multiple gallery images
  const [productImages, setProductImages] = useState<ProductGalleryImage[]>([])
  const [pendingImageFiles, setPendingImageFiles] = useState<File[]>([])
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)

  // Variants Manager State
  const [selectedProductForVariants, setSelectedProductForVariants] = useState<Product | null>(null)
  const [variantsList, setVariantsList] = useState<ProductVariant[]>([])

  // Variant Edit Modal State
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(null)
  const [editVariantPriceOverride, setEditVariantPriceOverride] = useState<string>('')
  const [editVariantCompareAtPrice, setEditVariantCompareAtPrice] = useState<string>('')
  const [editVariantStockQty, setEditVariantStockQty] = useState<number>(0)
  const [editVariantIsActive, setEditVariantIsActive] = useState<boolean>(true)
  const [savingVariantEdit, setSavingVariantEdit] = useState(false)

  const handleToggleVariantStatus = async (v: ProductVariant) => {
    try {
      await apiPut(`variants/${v.id}`, { isActive: !v.isActive })
      if (selectedProductForVariants) {
        loadVariants(selectedProductForVariants.id)
      }
    } catch (err) {
      alert((err as Error).message || 'Failed to update variant status.')
    }
  }

  const handleDeleteVariant = async (v: ProductVariant) => {
    if (!window.confirm(`Are you sure you want to remove variant '${v.sku}'?`)) return
    try {
      await apiDelete(`variants/${v.id}`)
      if (selectedProductForVariants) {
        loadVariants(selectedProductForVariants.id)
      }
    } catch (err) {
      alert((err as Error).message || 'Failed to remove variant.')
    }
  }

  const handleOpenEditVariant = (v: ProductVariant) => {
    setEditingVariant(v)
    setEditVariantPriceOverride(v.priceOverride != null ? String(v.priceOverride) : '')
    setEditVariantCompareAtPrice((v as { compareAtPrice?: number | null }).compareAtPrice != null ? String((v as { compareAtPrice?: number | null }).compareAtPrice) : '')
    setEditVariantStockQty(v.stockQty)
    setEditVariantIsActive(v.isActive)
  }

  const handleSaveVariantEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingVariant) return
    setSavingVariantEdit(true)
    try {
      await apiPut(`variants/${editingVariant.id}`, {
        priceOverride: editVariantPriceOverride !== '' ? Number(editVariantPriceOverride) : null,
        compareAtPrice: editVariantCompareAtPrice !== '' ? Number(editVariantCompareAtPrice) : null,
        stockQty: Number(editVariantStockQty),
        isActive: editVariantIsActive,
      })
      setEditingVariant(null)
      if (selectedProductForVariants) {
        loadVariants(selectedProductForVariants.id)
      }
    } catch (err) {
      alert((err as Error).message || 'Failed to update variant.')
    } finally {
      setSavingVariantEdit(false)
    }
  }

  const {
    register: registerVariant,
    handleSubmit: handleVariantFormSubmit,
    control: variantControl,
    reset: resetVariantForm,
    setValue: setVariantValue,
    formState: { errors: variantErrors },
  } = useForm<VariantFormValues>({
    resolver: zodResolver(VariantFormSchema),
    defaultValues: {
      sku: '',
      priceOverride: '',
      compareAtPrice: '',
      stockQty: 10,
      attrTypeId: 'at-1', // Default: Ring size
      attrValue: '7',
    },
  })

  // Confirmation Modals
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')

  useEffect(() => {
    if (viewMode === 'list') {
      fetchProducts()
    }
  }, [search, category, page, viewMode])

  const fetchProducts = () => {
    setLoading(true)
    apiGetWithMeta<Product[]>('products', {
      search: search || undefined,
      category: category || undefined,
      page,
      pageSize: 10,
    })
      .then((res) => {
        setProducts(res.data || [])
        setTotal(res.meta?.total ?? 0)
        setTotalPages(res.meta?.totalPages ?? 1)
      })
      .catch((err) => console.error('Fetch products error:', err))
      .finally(() => setLoading(false))
  }

  const defaultCategory = categories[0]?.slug ?? 'RINGS'
  const productCategory = watchProduct('category')

  useEffect(() => {
    if (viewMode !== 'editor' || editingProduct) return
    apiGet<{ sku: string }>('products/next-sku', { category: productCategory || defaultCategory })
      .then((data) => setProductValue('sku', data.sku))
      .catch(() => {})
  }, [viewMode, editingProduct, productCategory, defaultCategory, setProductValue])

  const categoryFormOptions = useMemo(
    () => categories.map((cat) => ({
      value: cat.slug,
      label: cat.navLabel || cat.label,
    })),
    [categories],
  )

  const categoryFilterOptions = useMemo(
    () => [
      { value: '', label: 'All Categories' },
      ...categoryFormOptions,
    ],
    [categoryFormOptions],
  )

  // Open Full-Page Editor for Creating a Product
  const handleOpenCreate = () => {
    setEditingProduct(null)
    resetProductForm({
      name: '',
      description: '',
      category: defaultCategory,
      basePrice: 0,
      sku: '',
      stockQty: 0,
      isActive: true,
      isFeatured: false,
      heroImageUrl: '',
    })
    setPendingImageFiles([])
    setProductImages([])
    setImagePreviewUrl(null)
    setError('')
    setSuccessMsg('')
    setViewMode('editor')
  }

  // Open Full-Page Editor for Editing an Existing Product
  const handleOpenEdit = async (product: Product) => {
    setEditingProduct(product)
    const rawPrice = product.basePrice ?? (product as any).base_price
    const priceNum = rawPrice !== undefined && rawPrice !== null ? Number(rawPrice) : 0
    const isActive = product.isActive ?? ((product as any).is_active === 1 || (product as any).is_active === true)
    const isFeatured = product.isFeatured ?? ((product as any).is_featured === 1 || (product as any).is_featured === true)
    const heroImg = product.heroImageUrl || (product as any).hero_image_url || ''

    resetProductForm({
      name: product.name,
      description: product.description || '',
      category: product.category,
      basePrice: priceNum,
      sku: product.sku,
      isActive: Boolean(isActive),
      isFeatured: Boolean(isFeatured),
      heroImageUrl: heroImg,
    })
    setPendingImageFiles([])
    setProductImages([])
    setImagePreviewUrl(heroImg || null)
    setError('')
    setSuccessMsg('')
    setViewMode('editor')

    try {
      const full = await apiGet<Product>(`products/${product.id}`)
      const images = full.images ?? []
      if (images.length > 0) {
        setProductImages(images)
        setImagePreviewUrl(images[0]!.url)
        setProductValue('heroImageUrl', images[0]!.url)
      } else if (heroImg) {
        setProductImages([{
          id: `hero-${product.id}`,
          productId: product.id,
          url: heroImg,
          altText: product.name,
          sortOrder: 0,
        }])
      }
    } catch {
      if (heroImg) {
        setProductImages([{
          id: `hero-${product.id}`,
          productId: product.id,
          url: heroImg,
          altText: product.name,
          sortOrder: 0,
        }])
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return

    const validFiles: File[] = []
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Each image must be 10MB or smaller.')
        continue
      }
      validFiles.push(file)
    }

    if (validFiles.length > 0) {
      setPendingImageFiles((prev) => [...prev, ...validFiles])
      if (!imagePreviewUrl) {
        setImagePreviewUrl(URL.createObjectURL(validFiles[0]!))
      }
      setError('')
    }

    e.target.value = ''
  }

  const handleRemoveSavedImage = async (imageId: string) => {
    if (!editingProduct?.id || imageId.startsWith('hero-')) return
    try {
      await apiDelete(`products/${editingProduct.id}/images/${imageId}`)
      setProductImages((prev) => {
        const next = prev.filter((img) => img.id !== imageId)
        const nextPreview = next[0]?.url ?? null
        setImagePreviewUrl(nextPreview)
        setProductValue('heroImageUrl', nextPreview ?? '')
        return next
      })
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to remove image.')
    }
  }

  const handleRemovePendingImage = (index: number) => {
    setPendingImageFiles((prev) => prev.filter((_, i) => i !== index))
  }

  // Save Product (Creates/Updates details + uploads multipart image if selected)
  const handleSaveProduct = async (data: CreateProductInputType) => {
    setError('')
    setSuccessMsg('')

    try {
      const payload = { ...data, heroImageUrl: data.heroImageUrl || undefined }
      const isNewProduct = !editingProduct?.id
      let targetProductId = editingProduct?.id
      let createdProductObj: Product | null = null

      if (targetProductId) {
        await apiPut(`products/${targetProductId}`, payload)
      } else {
        const created = await apiPost<Product>('products', payload)
        targetProductId = created.id
        createdProductObj = created
      }

      // Upload any newly selected gallery images
      if (pendingImageFiles.length > 0 && targetProductId) {
        for (let i = 0; i < pendingImageFiles.length; i++) {
          const uploadData = new FormData()
          uploadData.append('image', pendingImageFiles[i]!)
          uploadData.append('sort_order', String(productImages.length + i))
          await apiUpload<{ url: string }>(`products/${targetProductId}/images`, uploadData)
        }
      }

      if (isNewProduct && targetProductId) {
        setSuccessMsg(`Product '${data.name}' created successfully! Opening variant manager...`)
        const fullProduct = createdProductObj || (await apiGet<Product>(`products/${targetProductId}`))
        setTimeout(() => {
          handleOpenVariants(fullProduct)
          fetchProducts()
        }, 1000)
      } else {
        setSuccessMsg(`Product '${data.name}' saved successfully!`)
        setTimeout(() => {
          setViewMode('list')
          fetchProducts()
        }, 1200)
      }
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to save product details.')
    }
  }

  // Soft Archive Product
  const handleSoftDelete = async (id: string) => {
    try {
      await apiDelete(`products/${id}`)
      setDeleteConfirmId(null)
      fetchProducts()
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to soft-delete product.')
    }
  }

  // Open Variants Full-Page View
  const handleOpenVariants = async (product: Product) => {
    setSelectedProductForVariants(product)
    setViewMode('variants')
    loadVariants(product.id)
  }

  const loadVariants = async (productId: string) => {
    try {
      const data = await apiGet<ProductVariant[]>('variants', { productId })
      setVariantsList(data)
      const next = await apiGet<{ sku: string }>('variants/next-sku', { productId })
      setVariantValue('sku', next.sku)
    } catch (err) {
      console.error('Failed to load variants:', err)
    }
  }

  const handleAddVariant = async (data: VariantFormValues) => {
    if (!selectedProductForVariants) return

    try {
      await apiPost('variants', {
        productId: selectedProductForVariants.id,
        sku: data.sku || undefined,
        priceOverride: data.priceOverride ? parseFloat(data.priceOverride) : null,
        compareAtPrice: data.compareAtPrice ? parseFloat(data.compareAtPrice) : null,
        stockQty: data.stockQty,
        attributes: [
          {
            attrTypeId: data.attrTypeId,
            attrValue: data.attrValue,
          },
        ],
      })
      resetVariantForm({
        sku: '',
        priceOverride: '',
        stockQty: 10,
        attrTypeId: 'at-1',
        attrValue: '8',
      })
      loadVariants(selectedProductForVariants.id)
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to add variant.')
    }
  }

  const formatCurrency = (amt: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt)

  const productColumns = useMemo(
    () => [
      {
        name: 'Hero Image',
        cell: (p: Product) => {
          const heroImg = p.heroImageUrl || (p as { hero_image_url?: string }).hero_image_url
          return (
            <ProductImage
              src={heroImg ?? null}
              alt={p.name}
              className="w-11 h-11 rounded-xl shrink-0"
              imgClassName="w-11 h-11 object-cover rounded-xl bg-[var(--bg-main)] border border-[var(--border)] shadow-sm"
            />
          )
        },
        width: '90px',
      },
      {
        name: 'SKU Code',
        sortable: true,
        sortValue: (p: Product) => p.sku,
        selector: (p: Product) => (
          <span className="font-mono text-xs text-[var(--gold-main)] font-bold">{p.sku}</span>
        ),
      },
      {
        name: 'Product Title',
        sortable: true,
        sortValue: (p: Product) => p.name,
        cell: (p: Product) => {
          const isFeatured = p.isFeatured ?? ((p as { is_featured?: number | boolean }).is_featured === 1 || (p as { is_featured?: boolean }).is_featured === true)
          return (
            <div className="font-medium">
              {p.name}
              {isFeatured && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-[var(--gold-main)]/20 text-[var(--gold-main)] text-[9px] font-extrabold border border-[var(--gold-main)]/30">
                  FEATURED
                </span>
              )}
            </div>
          )
        },
      },
      {
        name: 'Category',
        sortable: true,
        sortValue: (p: Product) => getCategoryLabel(p.category, categories),
        selector: (p: Product) => getCategoryLabel(p.category, categories),
      },
      {
        name: 'Base Price',
        sortable: true,
        sortValue: (p: Product) => (typeof p.basePrice === 'number' ? p.basePrice : Number((p as { base_price?: number }).base_price ?? 0)),
        selector: (p: Product) => {
          const priceVal = typeof p.basePrice === 'number' ? p.basePrice : Number((p as { base_price?: number }).base_price ?? 0)
          return <span className="font-title font-bold">{formatCurrency(priceVal)}</span>
        },
      },
      {
        name: 'Status',
        cell: (p: Product) => {
          const isActive = p.isActive ?? ((p as { is_active?: number | boolean }).is_active === 1 || (p as { is_active?: boolean }).is_active === true)
          return (
            <span className={`status-badge ${isActive ? 'status-badge--active' : 'status-badge--inactive'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          )
        },
      },
      {
        name: 'Actions',
        right: true,
        cell: (p: Product) => (
          <div className="flex items-center justify-end gap-1.5">
            <button onClick={() => handleOpenVariants(p)} className="admin-icon-btn admin-icon-btn--gold" title="Manage Variants">
              <Layers className="w-4 h-4" />
            </button>
            <button onClick={() => handleOpenEdit(p)} className="admin-icon-btn admin-icon-btn--edit" title="Edit Product (Full Page)">
              <Edit2 className="w-4 h-4" />
            </button>
            <button onClick={() => setDeleteConfirmId(p.id)} className="admin-icon-btn admin-icon-btn--danger" title="Soft Archive Product">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [categories],
  )

  // =========================================================================
  // VIEW MODE 2: DEDICATED FULL-PAGE PRODUCT EDITOR FORM
  // =========================================================================
  if (viewMode === 'editor') {
    return (
      <div className="space-y-6 animate-fadeIn max-w-5xl mx-auto">
        {/* Editor Top Navigation Header Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setViewMode('list')}
              className="p-2 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)] hover:bg-[var(--bg-surface-3)] text-[var(--text-primary)] transition"
              title="Back to Products List"
            >
              <ArrowLeft className="w-5 h-5 text-[var(--gold-main)]" />
            </button>
            <div>
              <h2 className="text-xl sm:text-2xl font-title font-bold text-[var(--text-primary)] tracking-tight">
                {editingProduct ? `Edit Product: ${editingProduct.name}` : 'Create New Product'}
              </h2>
              <p className="text-xs text-[var(--text-muted)]">
                Configure catalogue information, pricing, inventory flags &amp; multipart R2 hero image.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className="btn-gold-outline text-xs py-2.5 px-4"
            >
              Cancel
            </button>
            <button
              onClick={handleProductFormSubmit(handleSaveProduct)}
              disabled={savingProduct}
              className="btn-gold text-xs py-2.5 px-6 font-bold flex items-center gap-2 shadow-lg"
            >
              {savingProduct ? (
                <div className="w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Product</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Feedback Notifications */}
        {error && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-300 flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {successMsg && (
          <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-300 flex items-center gap-3">
            <Check className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Editor Main Grid */}
        <form onSubmit={handleProductFormSubmit(handleSaveProduct)} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Basic Details & Pricing */}
          <div className="lg:col-span-8 space-y-6">
            {/* Basic Information Card */}
            <div className="glass-luxury-card p-6 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-[var(--gold-main)] uppercase tracking-wider border-b border-[var(--border)] pb-3">
                <Tag className="w-4 h-4" />
                <span>Basic Product Information</span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  {...registerProduct('name')}
                  placeholder="e.g. Royal Kundan Solitaire Ring (Gold Plated)"
                  aria-invalid={!!productErrors.name}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] placeholder-[var(--text-faint)] transition outline-none"
                />
                {productErrors.name && (
                  <span className="text-[11px] text-red-400 mt-1 block">{productErrors.name.message}</span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                    Category *
                  </label>
                  <Controller
                    control={productControl}
                    name="category"
                    render={({ field }) => (
                      <SearchableSelect
                        value={field.value}
                        onChange={field.onChange}
                        options={categoryFormOptions}
                        placeholder="Select category"
                        aria-label="Product category"
                        className="w-full"
                      />
                    )}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                    SKU Code (auto)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      {...registerProduct('sku')}
                      readOnly
                      placeholder="Generating…"
                      aria-invalid={!!productErrors.sku}
                      className="w-full bg-[var(--bg-surface-2)] border border-[var(--border)] rounded-xl py-3 pl-4 pr-4 text-xs text-[var(--gold-main)] font-mono placeholder-[var(--text-faint)] outline-none"
                    />
                  </div>
                  <span className="text-[10px] text-[var(--text-muted)] mt-1 block">
                    Assigned by the server when you open this form. Changes with category.
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                  Product Description
                </label>
                <textarea
                  {...registerProduct('description')}
                  rows={4}
                  placeholder="Detailed product specification, material composition, plating warranty, and craftsmanship details..."
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] placeholder-[var(--text-faint)] transition outline-none"
                />
              </div>
            </div>

            {/* Pricing & Store Settings Card */}
            <div className="glass-luxury-card p-6 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-[var(--gold-main)] uppercase tracking-wider border-b border-[var(--border)] pb-3">
                <DollarSign className="w-4 h-4" />
                <span>Pricing &amp; Display Flags</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2 flex items-center justify-between">
                    <span>Offer Price (INR ₹) *</span>
                    <span className="text-[10px] text-[var(--gold-main)] font-normal font-sans">Selling Price</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      {...registerProduct('basePrice', {
                        valueAsNumber: true,
                        onChange: (e) => {
                          e.target.value = e.target.value.replace(/[^0-9.]/g, '')
                        },
                      })}
                      onFocus={(e) => e.target.select()}
                      placeholder="e.g. 149"
                      aria-invalid={!!productErrors.basePrice}
                      className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 pl-8 pr-4 text-xs text-[var(--text-primary)] font-mono font-bold transition outline-none"
                    />
                    <span className="absolute left-3 top-3 text-xs text-[var(--gold-main)] font-bold">₹</span>
                  </div>
                  {productErrors.basePrice && (
                    <span className="text-[11px] text-red-400 mt-1 block">{productErrors.basePrice.message}</span>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2 flex items-center justify-between">
                    <span>Compare At Price / MRP (₹)</span>
                    <span className="text-[10px] text-[var(--text-muted)] font-normal font-sans">Actual Rate (Optional)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      {...registerProduct('compareAtPrice', {
                        valueAsNumber: true,
                        onChange: (e) => {
                          e.target.value = e.target.value.replace(/[^0-9.]/g, '')
                        },
                      })}
                      onFocus={(e) => e.target.select()}
                      placeholder="e.g. 199"
                      className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 pl-8 pr-4 text-xs text-[var(--text-primary)] font-mono transition outline-none"
                    />
                    <span className="absolute left-3 top-3 text-xs text-[var(--text-muted)] font-bold">₹</span>
                  </div>
                  <p className="text-[10px] text-[var(--text-faint)] mt-1">
                    Enter higher MRP rate (e.g. ₹199) to display crossed out strikethrough price &amp; % OFF badge.
                  </p>
                </div>
                {!editingProduct && (
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                      Opening stock (product)
                    </label>
                    <input
                      type="number"
                      min={0}
                      {...registerProduct('stockQty', { valueAsNumber: true })}
                      className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] font-mono outline-none"
                    />
                    <span className="text-[10px] text-[var(--text-muted)] mt-1 block">
                      Restock later from Stock Control even without variants.
                    </span>
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-[var(--border)] grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex items-center gap-3 p-3.5 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)] hover:border-[var(--gold-main)]/40 cursor-pointer transition">
                  <input
                    type="checkbox"
                    {...registerProduct('isActive')}
                    className="w-4 h-4 rounded bg-[var(--bg-main)] border-[var(--border)] text-[var(--gold-main)] focus:ring-0"
                  />
                  <div>
                    <div className="text-xs font-bold text-[var(--text-primary)]">Active in Storefront</div>
                    <div className="text-[10px] text-[var(--text-faint)]">Visible to customers in public catalogue</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3.5 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)] hover:border-[var(--gold-main)]/40 cursor-pointer transition">
                  <input
                    type="checkbox"
                    {...registerProduct('isFeatured')}
                    className="w-4 h-4 rounded bg-[var(--bg-main)] border-[var(--border)] text-[var(--gold-main)] focus:ring-0"
                  />
                  <div>
                    <div className="text-xs font-bold text-[var(--text-primary)]">Featured Collection</div>
                    <div className="text-[10px] text-[var(--text-faint)]">Promoted on storefront hero showcase</div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Right Column: Hero Image & R2 Multipart Upload */}
          <div className="lg:col-span-4 space-y-6">
            <div className="glass-luxury-card p-6 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-[var(--gold-main)] uppercase tracking-wider border-b border-[var(--border)] pb-3">
                <ImageIcon className="w-4 h-4" />
                <span>Product Gallery (R2)</span>
              </div>

              <div className="aspect-square w-full rounded-2xl bg-[var(--bg-main)] border border-[var(--border)] overflow-hidden relative flex items-center justify-center group shadow-inner">
                {imagePreviewUrl ? (
                  <img
                    src={assetSrc(imagePreviewUrl)}
                    alt="Preview"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition"
                  />
                ) : (
                  <div className="text-center p-6 space-y-2">
                    <Upload className="w-10 h-10 text-[var(--gold-main)]/50 mx-auto" />
                    <p className="text-xs text-[var(--text-faint)] font-medium">No Images Yet</p>
                    <p className="text-[10px] text-[var(--text-faint)]">JPEG, PNG or WebP up to 10MB each</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                  Upload Images (multiple)
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleFileSelect}
                  className="block w-full text-xs text-[var(--text-muted)] file:mr-3 file:py-2 px-3 py-2 rounded-xl bg-[var(--bg-main)] border border-[var(--border)] file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[var(--gold-main)] file:text-slate-950 hover:file:brightness-110 cursor-pointer"
                />
              </div>

              {(productImages.length > 0 || pendingImageFiles.length > 0) && (
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                    Gallery ({productImages.length + pendingImageFiles.length})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {productImages.map((img) => (
                      <div key={img.id} className="relative group rounded-lg overflow-hidden border border-[var(--border)] aspect-square">
                        <button
                          type="button"
                          onClick={() => setImagePreviewUrl(img.url)}
                          className="w-full h-full"
                        >
                          <img src={assetSrc(img.url)} alt="" className="w-full h-full object-cover" />
                        </button>
                        {!img.id.startsWith('hero-') && (
                          <button
                            type="button"
                            onClick={() => handleRemoveSavedImage(img.id)}
                            className="absolute top-1 right-1 p-1 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition"
                            title="Remove image"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    {pendingImageFiles.map((file, index) => (
                      <div key={`pending-${index}`} className="relative group rounded-lg overflow-hidden border border-dashed border-[var(--gold-main)]/50 aspect-square">
                        <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover opacity-80" />
                        <span className="absolute bottom-1 left-1 text-[8px] bg-[var(--gold-main)] text-slate-950 px-1 rounded font-bold">NEW</span>
                        <button
                          type="button"
                          onClick={() => handleRemovePendingImage(index)}
                          className="absolute top-1 right-1 p-1 rounded-full bg-red-500/90 text-white"
                          title="Remove"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-[var(--border)]">
                <label className="block text-[11px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5">
                  Or Primary Image CDN URL
                </label>
                <input
                  type="url"
                  {...registerProduct('heroImageUrl', {
                    onChange: (e) => {
                      if (pendingImageFiles.length === 0 && productImages.length === 0) {
                        setImagePreviewUrl(e.target.value || null)
                      }
                    },
                  })}
                  placeholder="https://images.unsplash.com/..."
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] font-mono placeholder-[var(--text-faint)] transition outline-none"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    )
  }

  // =========================================================================
  // VIEW MODE 3: DEDICATED FULL-PAGE VARIANT & INVENTORY MANAGER
  // =========================================================================
  if (viewMode === 'variants' && selectedProductForVariants) {
    return (
      <div className="space-y-6 animate-fadeIn max-w-5xl mx-auto">
        <div className="flex items-center justify-between pb-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setViewMode('list')}
              className="p-2 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)] hover:bg-[var(--bg-surface-3)] text-[var(--text-primary)] transition"
              title="Back to Products List"
            >
              <ArrowLeft className="w-5 h-5 text-[var(--gold-main)]" />
            </button>
            <div>
              <h2 className="text-xl sm:text-2xl font-title font-bold text-[var(--text-primary)] tracking-tight">
                Variants for: {selectedProductForVariants.name}
              </h2>
              <p className="text-xs text-[var(--gold-main)] font-mono mt-0.5">
                Base SKU: {selectedProductForVariants.sku} | Base Price: {formatCurrency(selectedProductForVariants.basePrice)}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Variants Table Column */}
          <div className="lg:col-span-7 space-y-4">
            <div className="glass-luxury-card p-6 space-y-4">
              <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider flex items-center gap-2">
                <Layers className="w-4 h-4 text-[var(--gold-main)]" />
                <span>Existing Stock Variants ({variantsList.length})</span>
              </h3>

              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                {variantsList.length === 0 ? (
                  <div className="p-8 text-center text-xs text-[var(--text-faint)] border border-dashed border-[var(--border)] rounded-xl">
                    No variants created yet for this product.
                  </div>
                ) : (
                  variantsList.map((v) => (
                    <div
                      key={v.id}
                      className={`p-4 rounded-xl border transition flex flex-col gap-3 ${
                        v.isActive
                          ? 'bg-[var(--bg-surface-2)] border-[var(--border)] hover:border-[var(--gold-main)]/40'
                          : 'bg-red-500/5 border-red-500/30 opacity-75'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-[var(--gold-main)] text-xs">{v.sku}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                              v.isActive ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/30'
                            }`}>
                              {v.isActive ? 'Active' : 'Disabled'}
                            </span>
                          </div>
                          <div className="text-xs font-medium text-[var(--text-primary)] mt-1">
                            {v.attributes?.map((a) => `${a.attrTypeName || 'Attribute'}: ${a.attrValue}`).join(', ') || 'Standard Variant'}
                          </div>
                        </div>

                        <div className="text-right">
                          <div className={`text-xs font-bold ${v.stockQty > 0 ? 'text-emerald-400' : 'text-amber-500'}`}>
                            {v.stockQty} Units Available
                          </div>
                          <div className="flex items-center justify-end gap-1.5 mt-0.5 font-mono text-[11px]">
                            {v.priceOverride != null && (
                              <span className="text-[var(--gold-main)] font-bold">Offer: ₹{v.priceOverride.toLocaleString('en-IN')}</span>
                            )}
                            {(v as { compareAtPrice?: number | null }).compareAtPrice != null && (
                              <span className="text-[var(--text-muted)] line-through decoration-red-400">
                                MRP: ₹{(v as { compareAtPrice?: number | null }).compareAtPrice?.toLocaleString('en-IN')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Chips: Enable/Disable, Edit, Remove */}
                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--border)]">
                        <button
                          type="button"
                          onClick={() => handleToggleVariantStatus(v)}
                          className="admin-action-chip admin-action-chip--secondary text-[11px]"
                          title={v.isActive ? 'Disable variant' : 'Enable variant'}
                        >
                          <Power className={`w-3.5 h-3.5 ${v.isActive ? 'text-emerald-400' : 'text-zinc-400'}`} />
                          <span>{v.isActive ? 'Disable' : 'Enable'}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleOpenEditVariant(v)}
                          className="admin-action-chip admin-action-chip--secondary text-[11px]"
                        >
                          <Edit2 className="w-3.5 h-3.5" /> Edit
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteVariant(v)}
                          className="admin-action-chip admin-action-chip--danger text-[11px]"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Add Variant Form Column */}
          <div className="lg:col-span-5 space-y-4">
            <div className="glass-luxury-card p-6 space-y-4">
              <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider flex items-center gap-2 border-b border-[var(--border)] pb-3">
                <Plus className="w-4 h-4 text-[var(--gold-main)]" />
                <span>Create New SKU Variant</span>
              </h3>

              <form onSubmit={handleVariantFormSubmit(handleAddVariant)} className="space-y-4 text-xs">
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                    Variant SKU Code *
                  </label>
                  <input
                    type="text"
                    {...registerVariant('sku', {
                      onChange: (e) => {
                        e.target.value = e.target.value.toUpperCase()
                      },
                    })}
                    placeholder="BC-RING-001-SZ10"
                    aria-invalid={!!variantErrors.sku}
                    className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] font-mono transition outline-none"
                  />
                  {variantErrors.sku && (
                    <span className="text-[11px] text-red-400 mt-1 block">{variantErrors.sku.message}</span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                      Initial Stock *
                    </label>
                    <input
                      type="number"
                      {...registerVariant('stockQty')}
                      min="0"
                      className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] font-mono font-bold transition outline-none"
                    />
                    {variantErrors.stockQty && (
                      <span className="text-[11px] text-red-400 mt-1 block">{variantErrors.stockQty.message}</span>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                      Price Override (₹)
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      {...registerVariant('priceOverride', {
                        onChange: (e) => {
                          e.target.value = e.target.value.replace(/[^0-9.]/g, '')
                        },
                      })}
                      onFocus={(e) => e.target.select()}
                      placeholder="e.g. 149"
                      className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] font-mono transition outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                    Compare At Price / MRP (Actual Rate ₹)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    {...registerVariant('compareAtPrice', {
                      onChange: (e) => {
                        e.target.value = e.target.value.replace(/[^0-9.]/g, '')
                      },
                    })}
                    onFocus={(e) => e.target.select()}
                    placeholder="e.g. 199"
                    className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] font-mono transition outline-none"
                  />
                  <p className="text-[10px] text-[var(--text-faint)] mt-1">
                    Enter higher MRP (e.g. ₹199) to display crossed out strikethrough price.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                      Attribute Type
                    </label>
                    <Controller
                      control={variantControl}
                      name="attrTypeId"
                      render={({ field }) => (
                        <SearchableSelect
                          value={field.value}
                          onChange={field.onChange}
                          options={VARIANT_ATTR_OPTIONS}
                          placeholder="Attribute type"
                          aria-label="Variant attribute type"
                          className="w-full"
                        />
                      )}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2">
                      Attribute Value
                    </label>
                    <input
                      type="text"
                      {...registerVariant('attrValue')}
                      placeholder="e.g. Size 7 or Gold Plated Covering"
                      aria-invalid={!!variantErrors.attrValue}
                      className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] transition outline-none"
                    />
                    {variantErrors.attrValue && (
                      <span className="text-[11px] text-red-400 mt-1 block">{variantErrors.attrValue.message}</span>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full btn-gold py-3 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Variant SKU</span>
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Edit Variant Dialog Modal */}
        {editingVariant && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
            <div className="bg-[var(--bg-card)] border border-[var(--border-gold)] shadow-2xl rounded-2xl w-full max-w-md p-6 space-y-5 my-auto">
              <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
                <h3 className="text-base font-title font-bold text-[var(--text-primary)] flex items-center gap-2">
                  <Edit2 className="w-4 h-4 text-[var(--gold-main)]" />
                  <span>Edit Variant: {editingVariant.sku}</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setEditingVariant(null)}
                  className="p-1 rounded-lg text-[var(--text-muted)] hover:bg-[var(--bg-surface-2)] transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSaveVariantEdit} className="space-y-4 text-xs">
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-1.5 flex items-center justify-between">
                    <span>Offer Price Override (₹)</span>
                    <span className="text-[10px] text-[var(--gold-main)] font-normal font-sans">Selling Price</span>
                  </label>
                  <input
                    type="number"
                    value={editVariantPriceOverride}
                    onChange={(e) => setEditVariantPriceOverride(e.target.value)}
                    placeholder="Leave blank to use base product price"
                    className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-2.5 px-3 text-xs text-[var(--text-primary)] font-mono transition outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-1.5 flex items-center justify-between">
                    <span>Compare At Price / MRP (₹)</span>
                    <span className="text-[10px] text-[var(--text-muted)] font-normal font-sans">Actual Rate (Optional)</span>
                  </label>
                  <input
                    type="number"
                    value={editVariantCompareAtPrice}
                    onChange={(e) => setEditVariantCompareAtPrice(e.target.value)}
                    placeholder="e.g. 199"
                    className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-2.5 px-3 text-xs text-[var(--text-primary)] font-mono transition outline-none"
                  />
                  <p className="text-[10px] text-[var(--text-faint)] mt-1">
                    Enter higher MRP (e.g. ₹199) to display crossed out strikethrough price.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-1.5">
                    Available Stock Quantity *
                  </label>
                  <input
                    type="number"
                    value={editVariantStockQty}
                    onChange={(e) => setEditVariantStockQty(Number(e.target.value))}
                    min={0}
                    className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-2.5 px-3 text-xs text-[var(--text-primary)] font-mono font-bold transition outline-none"
                    required
                  />
                </div>

                <div className="flex items-center gap-2.5 pt-2">
                  <input
                    type="checkbox"
                    id="editVariantIsActive"
                    checked={editVariantIsActive}
                    onChange={(e) => setEditVariantIsActive(e.target.checked)}
                    className="w-4 h-4 rounded border-[var(--border)] text-[var(--gold-main)] cursor-pointer"
                  />
                  <label htmlFor="editVariantIsActive" className="text-xs font-medium cursor-pointer text-[var(--text-primary)]">
                    Enable and publish this variant for storefront orders
                  </label>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border)]">
                  <button
                    type="button"
                    onClick={() => setEditingVariant(null)}
                    className="btn-gold-outline text-xs py-2 px-4 rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingVariantEdit}
                    className="btn-gold text-xs py-2 px-5 font-bold flex items-center gap-1.5 rounded-xl"
                  >
                    {savingVariantEdit ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    <span>Save Variant</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    )
  }

  // =========================================================================
  // VIEW MODE 1: PRODUCTS LIST TABLE VIEW
  // =========================================================================
  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Top Header Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-title font-bold text-[var(--text-primary)] tracking-tight">
            Product Catalogue &amp; Inventory
          </h2>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Manage catalogue items, pricing, R2 asset uploads &amp; SKU variant stock.
          </p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="btn-gold text-xs py-2.5 px-5 font-bold flex items-center gap-2 shadow-lg"
        >
          <Plus className="w-4 h-4" />
          <span>Add New Product</span>
        </button>
      </div>

      {/* Filter & Search Toolbar */}
      <div className="glass-luxury-card p-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative flex-1 w-full">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search catalogue by name, SKU, or category..."
            className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] focus:ring-1 focus:ring-[var(--gold-main)] rounded-xl py-2.5 pl-10 pr-4 text-xs text-[var(--text-primary)] placeholder-[var(--text-faint)] transition outline-none"
          />
          <Search className="absolute left-3.5 top-3 w-4 h-4 text-[var(--gold-main)]" />
        </div>

        <SearchableSelect
          value={category}
          onChange={(v) => { setCategory(v); setPage(1) }}
          options={categoryFilterOptions}
          placeholder="All Categories"
          aria-label="Filter by category"
          className="w-full sm:w-56"
        />
      </div>

      {/* Products — desktop table */}
      <div className="glass-luxury-card overflow-hidden hidden md:block">
        <AppDataTable
          columns={productColumns}
          data={products}
          keyField={(p) => p.id}
          progressPending={loading}
          noDataComponent="No products found in database matching filter criteria."
        />
      </div>

      {/* Products — mobile cards */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="admin-mobile-card text-center py-10">
            <div className="w-8 h-8 border-2 border-[var(--gold-main)] border-t-transparent rounded-full animate-spin mx-auto" />
            <span className="text-xs text-[var(--text-faint)] mt-2 block">Loading products…</span>
          </div>
        ) : products.length === 0 ? (
          <div className="admin-mobile-card text-center py-10 text-xs text-[var(--text-faint)]">
            No products found matching filter criteria.
          </div>
        ) : (
          products.map((p) => {
            const priceVal = typeof p.basePrice === 'number' ? p.basePrice : Number((p as any).base_price ?? 0)
            const isActive = p.isActive ?? ((p as any).is_active === 1 || (p as any).is_active === true)
            const isFeatured = p.isFeatured ?? ((p as any).is_featured === 1 || (p as any).is_featured === true)
            const heroImg = p.heroImageUrl || (p as any).hero_image_url

            return (
              <div key={p.id} className="admin-mobile-card">
                <div className="flex gap-3">
                  <ProductImage
                    src={heroImg ?? null}
                    alt={p.name}
                    className="w-14 h-14 rounded-xl shrink-0"
                    imgClassName="w-14 h-14 object-cover rounded-xl border border-[var(--border)]"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[10px] text-[var(--gold-main)] font-bold">{p.sku}</p>
                    <p className="text-sm font-medium text-[var(--text-primary)] line-clamp-2 mt-0.5">
                      {p.name}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {isFeatured && (
                        <span className="px-2 py-0.5 rounded-full bg-[var(--gold-main)]/20 text-[var(--gold-main)] text-[9px] font-extrabold border border-[var(--gold-main)]/30">
                          FEATURED
                        </span>
                      )}
                      <span className="text-[10px] text-[var(--text-muted)]">{getCategoryLabel(p.category, categories)}</span>
                      <span className={`status-badge ${isActive ? 'status-badge--active' : 'status-badge--inactive'}`}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm font-title font-bold text-[var(--text-primary)] shrink-0">
                    {formatCurrency(priceVal)}
                  </p>
                </div>
                <div className="admin-mobile-card-actions">
                  <button
                    type="button"
                    onClick={() => handleOpenVariants(p)}
                    className="admin-action-chip admin-action-chip--gold"
                  >
                    <Layers className="w-3.5 h-3.5" /> Variants
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenEdit(p)}
                    className="admin-action-chip admin-action-chip--edit"
                  >
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmId(p.id)}
                    className="admin-action-chip admin-action-chip--danger"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>

      <DataTablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={10}
        onPageChange={setPage}
        className="pt-2"
      />

      {/* SOFT DELETE CONFIRMATION MODAL */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="glass-luxury-card max-w-sm w-full p-6 text-center space-y-4 border-red-500/40">
            <AlertTriangle className="w-10 h-10 text-red-400 mx-auto" />
            <div>
              <h3 className="text-lg font-title font-bold text-[var(--text-primary)]">Archive Product?</h3>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Per zero-data-loss rules, this product will be soft-deleted (`deleted_at` timestamp set) and hidden from the active store catalog.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="btn-gold-outline text-xs py-2 px-4 flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSoftDelete(deleteConfirmId)}
                className="btn-gold bg-red-600 text-[var(--text-primary)] border-0 text-xs py-2 px-4 flex-1 font-bold"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
